import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";

import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface Props {
  tableHeadings: { [key: string]: string };
  budgetOveruse: boolean;
}

type StatusStyle = { bg: string; color: string; border: string };

const green: StatusStyle = {
  bg: "#EDF7ED",
  color: "#2E7D32",
  border: "#2E7D3280",
};
const yellow: StatusStyle = {
  bg: "#FFFAEB",
  color: "#DC6803",
  border: "#ffc593",
};
const red: StatusStyle = {
  bg: "#FDEDED",
  color: "#D32F2F",
  border: "#D32F2F80",
};

// Tooltips keyed by column key
const headerTooltips: { [key: string]: string } = {
  qftd_budget: "Based on the CPA budget divided by the current deal",
};

const getStatusStyleForKey = (key: string, percent: number): StatusStyle => {
  switch (key) {
    case "perc_of_budget_used":
      // Red if ≥100%, Yellow if 80%–99%, Green otherwise
      if (percent >= 100) return red;
      if (percent >= 80) return yellow;
      return green;

    case "perc_of_qftd_budget_used":
      // Red if ≥100%, Green otherwise
      if (percent >= 100) return red;
      return green;

    case "perc_of_qftd_RR_budget_use":
      // Red if ≥100%, Green otherwise
      if (percent >= 100) return red;
      return green;

    default:
      return green;
  }
};

//rounded down to the nearest integer(https://optival.atlassian.net/browse/GR-21152)
// QFTD budget: rounded down to nearest integer; empty when missing
const roundDownBudget = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isFinite(num)
    ? `$${Math.floor(num).toLocaleString("en-US")}`
    : "";
};

const cleanNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  return parseFloat(String(value).replace(/[% ,]/g, "")) || 0;
};

const BudgetControlTable = ({ tableHeadings, budgetOveruse }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const headCells = React.useMemo(
    () => Object.entries(tableHeadings),
    [tableHeadings],
  );
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "desc" });

  // Sync default sort key once headCells are available
  React.useEffect(() => {
    if (headCells.length > 0 && !sort.keytoSort) {
      setSort({ keytoSort: headCells[0][0], direction: "desc" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headCells]);

  // Fetched budget data from Redux, not undefined variable
  const rows: any[] =
    useSelector(
      (state: any) => state.bizDevelopmentDashboard.budgetDataOfBizDevelopment,
    ) ?? [];

  // Fetched brand name from Redux
  const brandNameForBudget = useSelector(
    (state: any) => state.bizDevelopmentDashboard.brandNameForBudget,
  );

  // Keys checked for budget overuse filter (any one ≥ 100% qualifies the row)
  const budgetOveruseKeys = ["perc_of_budget_used"];

  // Filter rows based on budget overuse toggle
  const filteredRows = React.useMemo(() => {
    const search = (brandNameForBudget ?? "").trim().toLowerCase();

    let result = rows;

    // Brand name filter (matches brand_display_name)
    if (search) {
      result = result.filter((row) =>
        String(row.brand_display_name ?? "")
          .toLowerCase()
          .includes(search),
      );
    }

    // Budget overuse filter
    if (budgetOveruse) {
      result = result.filter((row) =>
        budgetOveruseKeys.some((key) => cleanNumber(row[key]) * 100 >= 100),
      );
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, budgetOveruse, brandNameForBudget]);

  //reset to first page whenever the budget overuse toggle changes
  React.useEffect(() => {
    setPage(0);
  }, [budgetOveruse, brandNameForBudget]);

  const handleSort = (key: string) => {
    setSort((prev) => ({
      keytoSort: key,
      direction:
        prev.keytoSort === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aRaw = a[sort.keytoSort];
      const bRaw = b[sort.keytoSort];

      // Nulls always to bottom
      if (aRaw == null && bRaw == null) return 0;
      if (aRaw == null) return 1;
      if (bRaw == null) return -1;

      const aNum = cleanNumber(aRaw);
      const bNum = cleanNumber(bRaw);

      // If both parse to 0 and original values are non-numeric strings → sort alphabetically
      const isNumeric = (v: any) =>
        !isNaN(parseFloat(String(v).replace(/[% ,]/g, ""))) &&
        String(v).replace(/[% ,]/g, "") !== "";

      if (!isNumeric(aRaw) && !isNumeric(bRaw)) {
        return sort.direction === "asc"
          ? String(aRaw).localeCompare(String(bRaw))
          : String(bRaw).localeCompare(String(aRaw));
      }

      return sort.direction === "asc" ? aNum - bNum : bNum - aNum;
    });
  }, [filteredRows, sort]);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const renderPercentBadge = (key: string, value: any) => {
    if (value == null) return <>0</>;

    // API returns values as decimals (e.g. 0.955 = 95.5%), multiply by 100
    const percent = cleanNumber(value) * 100;
    const statusStyle = getStatusStyleForKey(key, percent);

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Box
          sx={{
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            border: `1px solid ${statusStyle.border}`,
            borderRadius: "4px",
            px: "6px",
            py: "1px",
            fontSize: "14px",
            fontWeight: 400,
          }}
        >
          {percent.toFixed(1)}%
        </Box>
      </Box>
    );
  };

  //Current requirements(https://optival.atlassian.net/browse/GR-21074)
  //negative value should be displayed as -$100
  //positive value should be displayed as $100
  // used 1000 seperator and displayed as $1,000
  // numver should be displayed as whole number(rounded number)
  const renderCurrencyCell = (value: any) => {
    if (value == null) return <>$0</>;

    const num = parseFloat(String(value).replace(/[, ]/g, ""));
    const formatted = Math.abs(num).toLocaleString("en-US", {
      maximumFractionDigits: 0,
    });

    return <>{num < 0 ? `-$${formatted}` : `$${formatted}`}</>;
  };

  // Keys that should render as percent badges
  const percentKeys = new Set([
    "perc_of_budget_used",
    "perc_of_qftd_RR_budget_use",
    "perc_of_qftd_budget_used",
  ]);

  // $ currency keys
  const currencyKeys = new Set([
    "cpa_constraint",
    "total_income",
    "cpa_income",
  ]);

  const renderCell = (key: string, row: any) => {
    const value = row[key];

    if (percentKeys.has(key)) return renderPercentBadge(key, value);
    if (currencyKeys.has(key)) return renderCurrencyCell(value);
    //new changes (https://optival.atlassian.net/browse/GR-21152)
    if (key === "remaining_qftd" && value != null)
      return <>{Math.trunc(Number(value))}</>;
    if (key === "qftd_budget" && value != null) return roundDownBudget(value);
    return <>{value ?? ""}</>;
  };

  return (
    <>
      <TableContainer>
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {headCells.map(([key, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === key}
                    direction={sort.keytoSort === key ? sort.direction : "desc"}
                    onClick={() => handleSort(key)}
                  >
                    {value && typeof value === "string" ? value : ""}
                    {headerTooltips[key] && (
                      <Tooltip
                        title={headerTooltips[key]}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 160,
                              whiteSpace: "normal",
                              textAlign: "center",
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            fontSize: 16,
                            color: "gray",
                            cursor: "pointer",
                            ml: 0.5,
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows?.length > 0 &&
              sortedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow
                    key={row.brand_display_name ?? rowIndex}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {renderCell(key, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            {sortedRows?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{ displayedRows: "hidden-displayed-rows" }}
          sx={{
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-input": { marginLeft: "15px" },
            ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
          }}
        />
        <Pagination
          count={Math.ceil(filteredRows.length / rowsPerPage)}
          page={page + 1}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-toolbar": { paddingRight: "16px" },
          }}
        />
      </Stack>
    </>
  );
};

export default BudgetControlTable;
